import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { Alert } from "../components/ui";

/**
 * Open industry registration. Creates the organisation and its first user
 * account in PENDING_APPROVAL; a coordinator approves before anything is visible.
 */
export default function Register() {
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const form = new FormData(e.currentTarget);
    try {
      await api.post("/companies/register", {
        companyName: form.get("companyName"),
        website: form.get("website"),
        contactName: form.get("contactName"),
        contactEmail: form.get("contactEmail"),
        contactPhone: form.get("contactPhone"),
        answers: {},
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md py-12">
        <Alert tone="success">
          Thanks — your registration has been received. An internship coordinator will review it and be in
          touch once your organisation is approved.
        </Alert>
        <a className="btn-secondary w-full" href="/signin">Back to sign in</a>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg py-12">
      <h1 className="mb-1 text-2xl font-semibold text-heading">Register your organisation</h1>
      <p className="mb-6 text-uoa-grey">
        Tell us about your organisation so we can approve you to post internship positions.
      </p>
      {error && <Alert tone="error">{error}</Alert>}

      <form onSubmit={submit} className="card space-y-4">
        <Field name="companyName" label="Organisation name" required />
        <Field name="website" label="Website" type="url" placeholder="https://" />
        <Field name="contactName" label="Your name" required />
        <Field name="contactEmail" label="Your work email" type="email" required autoComplete="email" />
        <Field name="contactPhone" label="Phone" type="tel" />
        {/* TODO: remaining industry form fields, pending the client's field list. */}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? "Submitting..." : "Submit registration"}
        </button>
      </form>
    </div>
  );
}

function Field({ name, label, ...rest }: { name: string; label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="label" htmlFor={name}>
        {label} {rest.required && <span aria-hidden className="text-red-700">*</span>}
      </label>
      <input id={name} name={name} className="input" {...rest} />
    </div>
  );
}
